import { ComplianceObjectType, ProposedChangeFieldKey } from "~/graphql/supergraphEnums";
import { checkIsUsCountrySelected } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AddressFields/StateFormField/utils";
import { findUsStateName } from "~/pages/Compliance/documents/CaseCreationPreview/stateMatcher";
import { accountTypeFromMainAccountType } from "~/pages/Compliance/utils/mainAccountTypeUtils";
import {
  accountViewFromNode,
  addressLines,
  categoryFromAccountType,
  comparisonStatus,
  isPresent,
  joinAddressLines,
  programsFromPositions,
  toCell,
  toLinesCell,
  toTagsCell,
  viewableAccountType,
} from "../CompareTable/utils";
import type { MatchCell, MatchStatus, ProfileMatchData, ProfileMatchRow } from "./types";
import type { WizardProfileAddress, WizardProfileData } from "../../types";
import type { AccountView, InvestmentAccountNode } from "../CompareTable/utils";
import type { AccountTypeEnum, ProfileComparisonQuery } from "~/graphql/supergraphTypes";

export type CountryNameResolver = (code: string | null | undefined) => string | null;

type RankedProfileNode = NonNullable<ProfileComparisonQuery["rankedProfile"]>;
export type ProfileNode = RankedProfileNode["profiles"][number];
type PerFieldMatchEntry = ProfileNode["perFieldMatch"][number];

export type ProfileView = {
  category: ComplianceObjectType;
  accountType: AccountTypeEnum | null;
  name: string | null;
  taxId: string | null;
  taxIdType: string | null;
  addressLines: string[];
  domicileCountry: string | null;
  positionCount: number;
  programs: string[];
};

const ACCOUNT_TYPE_LABEL: Record<ComplianceObjectType, string> = {
  [ComplianceObjectType.Individual]: "Individual",
  [ComplianceObjectType.Institution]: "Entity",
  [ComplianceObjectType.Joint]: "Joint ownership",
};

const ENTITY_TYPE_ROW_LABEL: Record<ComplianceObjectType, string> = {
  [ComplianceObjectType.Individual]: "Individual type",
  [ComplianceObjectType.Institution]: "Entity type",
  [ComplianceObjectType.Joint]: "Joint ownership type",
};

const isComplianceObjectType = (value: string): value is ComplianceObjectType =>
  Object.values(ComplianceObjectType).some((entry) => entry === value);

const isProposedChangeFieldKey = (value: string): value is ProposedChangeFieldKey =>
  Object.values(ProposedChangeFieldKey).some((entry) => entry === value);

export const profileCategoryFromNode = (profile: ProfileNode): ComplianceObjectType =>
  isComplianceObjectType(profile.complianceObjectType)
    ? profile.complianceObjectType
    : ComplianceObjectType.Individual;

// Individuals display as first/middle/last; everyone else displays their legal (business) name.
const fullOrLegalName = (
  isIndividual: boolean,
  firstName: string | null | undefined,
  middleName: string | null | undefined,
  lastName: string | null | undefined,
  legalOrBusinessName: string | null | undefined,
): string | null =>
  isIndividual
    ? [firstName, middleName, lastName].filter(isPresent).join(" ") || null
    : (legalOrBusinessName ?? null);

// Reads the profile's display values straight from its own fields, with no server comparison.
export const profileViewFromNode = (profile: ProfileNode): ProfileView => {
  const name = fullOrLegalName(
    profile.complianceObjectType === ComplianceObjectType.Individual,
    profile.firstName,
    profile.middleName,
    profile.lastName,
    profile.legalName,
  );
  const domicileAddress =
    profile.addresses.find((entry) => entry.isPrimaryDomicile) ?? profile.addresses[0];
  return {
    category: profileCategoryFromNode(profile),
    accountType: profile.accountType ?? null,
    name,
    taxId: profile.taxIdUnencrypted ?? null,
    taxIdType: profile.taxIdType?.toUpperCase() ?? null,
    addressLines: domicileAddress
      ? addressLines(
          [domicileAddress.street, domicileAddress.street2, domicileAddress.street3],
          domicileAddress.city,
          [domicileAddress.state?.name ?? domicileAddress.province, domicileAddress.postalCode],
          domicileAddress.country?.name,
        )
      : [],
    domicileCountry:
      domicileAddress?.country?.name ?? profile.organizationalJurisdiction?.name ?? null,
    positionCount: profile.positions.length,
    programs: programsFromPositions(profile.positions),
  };
};

// The wizard form stores a US state as a code (e.g. "CA"); expand it to a full name only for US
// addresses, since codes collide across countries (e.g. "WA" is also Western Australia).
const stateOrProvinceName = (address: WizardProfileAddress): string | undefined =>
  checkIsUsCountrySelected(address.country)
    ? (findUsStateName(address.stateOrProvince) ?? address.stateOrProvince)
    : address.stateOrProvince;

export const profileViewFromForm = (
  profile: WizardProfileData,
  resolveCountryName: CountryNameResolver,
): ProfileView => {
  const category = profile.category ?? ComplianceObjectType.Individual;
  const name = fullOrLegalName(
    category === ComplianceObjectType.Individual,
    profile.firstName,
    profile.middleName,
    profile.lastName,
    profile.legalBusinessName,
  );
  const address = profile.address;
  return {
    category,
    accountType: profile.accountType ?? null,
    name,
    taxId: profile.taxId ?? null,
    taxIdType: profile.taxIdType?.toUpperCase() ?? null,
    addressLines: address
      ? addressLines(
          [address.streetLine1, address.streetLine2],
          address.city,
          [stateOrProvinceName(address), address.zipCode],
          resolveCountryName(address.country),
        )
      : [],
    domicileCountry: resolveCountryName(profile.domicileCountry),
    positionCount: 0,
    programs: [],
  };
};

// A null `linkableProfileId` means a not-yet-created profile, which has no positions page to open.
const toExistingPositionsCell = (count: number, linkableProfileId: number | null): MatchCell =>
  linkableProfileId != null && count > 0
    ? { value: String(count), isLink: true, profileId: linkableProfileId }
    : toCell(String(count));

const emptyRow = (label: string): ProfileMatchRow => ({
  label,
  complianceProfile: {},
  investmentAccount: {},
  status: "na",
});

// The investment account's positions can span multiple owner profiles, so its count opens the
// modal scoped to the account (all its positions) rather than to a single profile.
const toAccountPositionsCell = (account: AccountView): MatchCell =>
  account.positionRows.length > 0
    ? {
        value: String(account.positionCount),
        isLink: true,
        accountPositions: {
          accountName: account.name ?? "",
          rows: account.positionRows,
          viewAllHref: account.positionsHref,
        },
      }
    : toCell(String(account.positionCount));

const positionsRow = (
  profilePositionCount: number,
  account: AccountView | null,
  linkableProfileId: number | null,
): ProfileMatchRow => ({
  label: "Existing positions",
  complianceProfile: toExistingPositionsCell(profilePositionCount, linkableProfileId),
  investmentAccount: account ? toAccountPositionsCell(account) : {},
  status: "na",
  excludeFromScore: true,
});

const complianceProgramsRow = (
  profilePrograms: string[],
  account: AccountView | null,
): ProfileMatchRow => ({
  label: "Compliance programs",
  complianceProfile: toTagsCell(profilePrograms),
  investmentAccount: account ? toTagsCell(account.programs) : {},
  status: "na",
  excludeFromScore: true,
});

const perFieldMatchByKey = (
  profileNode: ProfileNode,
): Map<ProposedChangeFieldKey, PerFieldMatchEntry> => {
  const byKey = new Map<ProposedChangeFieldKey, PerFieldMatchEntry>();
  for (const entry of profileNode.perFieldMatch) {
    if (isProposedChangeFieldKey(entry.fieldKey)) byKey.set(entry.fieldKey, entry);
  }
  return byKey;
};

const serverFieldRow = (
  fieldMatch: Map<ProposedChangeFieldKey, PerFieldMatchEntry>,
  label: string,
  fieldKey: ProposedChangeFieldKey,
): ProfileMatchRow => {
  const entry = fieldMatch.get(fieldKey);
  if (!entry) return emptyRow(label);
  return {
    label,
    complianceProfile: toCell(entry.profileValue),
    investmentAccount: toCell(entry.iaValue),
    status: entry.matches === null ? "na" : entry.matches ? "match" : "mismatch",
  };
};

// The server returns the raw AccountTypeEnum value (e.g. "llc") for entity_type; translate it
// to its human-readable label the same way the local comparison rows do.
const serverEntityTypeRow = (
  fieldMatch: Map<ProposedChangeFieldKey, PerFieldMatchEntry>,
  label: string,
): ProfileMatchRow => {
  const entry = fieldMatch.get(ProposedChangeFieldKey.EntityType);
  if (!entry) return emptyRow(label);
  return {
    label,
    complianceProfile: toCell(viewableAccountType(entry.profileValue)),
    investmentAccount: toCell(viewableAccountType(entry.iaValue)),
    status: entry.matches === null ? "na" : entry.matches ? "match" : "mismatch",
  };
};

const serverTaxIdRow = (
  fieldMatch: Map<ProposedChangeFieldKey, PerFieldMatchEntry>,
): ProfileMatchRow => {
  const taxId = fieldMatch.get(ProposedChangeFieldKey.TaxId);
  if (!taxId) return emptyRow("ID number");
  const taxIdType = fieldMatch.get(ProposedChangeFieldKey.TaxIdType);
  return {
    label: "ID number",
    complianceProfile: toCell(
      taxId.profileValue,
      taxIdType?.profileValue?.toUpperCase() ?? null,
    ),
    investmentAccount: toCell(taxId.iaValue, taxIdType?.iaValue?.toUpperCase() ?? null),
    status: taxId.matches === null ? "na" : taxId.matches ? "match" : "mismatch",
  };
};

// An existing profile's comparison is driven entirely by the server's per-field match: both the
// value and the status come from `perFieldMatch`. A field with no entry renders blank as "na".
export const buildServerComparisonRows = (
  profileNode: ProfileNode,
  account: AccountView | null,
  layout: ComplianceObjectType,
  linkableProfileId: number | null,
): ProfileMatchRow[] => {
  const fieldMatch = perFieldMatchByKey(profileNode);
  const rows: ProfileMatchRow[] = [
    serverFieldRow(fieldMatch, "Client", ProposedChangeFieldKey.ClientName),
    serverFieldRow(fieldMatch, "Account type", ProposedChangeFieldKey.AccountType),
    serverEntityTypeRow(fieldMatch, ENTITY_TYPE_ROW_LABEL[layout]),
    serverFieldRow(fieldMatch, "Name", ProposedChangeFieldKey.LegalName),
    // The backend decides whether tax id applies by including (or omitting) it in
    // `perFieldMatch`; defer to that instead of re-deriving it from `layout` here.
    ...(fieldMatch.has(ProposedChangeFieldKey.TaxId) ? [serverTaxIdRow(fieldMatch)] : []),
    // The domicile row's label reflects the investment account's own type: a jointly-owned
    // account only compares domicile at the country level, regardless of the profile's own type.
    serverFieldRow(
      fieldMatch,
      account?.category === ComplianceObjectType.Joint
        ? "Domicile country"
        : "Domicile address",
      ProposedChangeFieldKey.Address,
    ),
    positionsRow(profileNode.positions.length, account, linkableProfileId),
    complianceProgramsRow(programsFromPositions(profileNode.positions), account),
  ];
  // Without an account there is nothing for the server to have compared, regardless of what
  // `perFieldMatch` happens to contain.
  return account === null ? rows.map((row) => ({ ...row, status: "na" as const })) : rows;
};

// A blank on either side is not comparable, so it becomes "na" and drops out of the percentage.
const localStatus = (
  profileValue: string | null | undefined,
  accountValue: string | null | undefined,
): MatchStatus => comparisonStatus(profileValue, accountValue) ?? "na";

const localFieldRow = (
  label: string,
  profileValue?: string | null,
  accountValue?: string | null,
): ProfileMatchRow => ({
  label,
  complianceProfile: toCell(profileValue),
  investmentAccount: toCell(accountValue),
  status: localStatus(profileValue, accountValue),
});

const localTaxIdRow = (profile: ProfileView, account: AccountView | null): ProfileMatchRow => ({
  label: "ID number",
  complianceProfile: toCell(profile.taxId, profile.taxIdType),
  investmentAccount: toCell(account?.taxId ?? null),
  status: localStatus(profile.taxId, account?.taxId ?? null),
});

const localAddressRow = (
  profile: ProfileView,
  account: AccountView | null,
): ProfileMatchRow => {
  const profileAddress = joinAddressLines(profile.addressLines);
  const accountAddress = joinAddressLines(account?.addressLines ?? []);
  return {
    label: "Domicile address",
    complianceProfile: toLinesCell(profile.addressLines),
    investmentAccount: toLinesCell(account?.addressLines ?? []),
    status: localStatus(profileAddress, accountAddress),
  };
};

// A not-yet-created profile has no server comparison to read, so its rows compare the profile's
// own display values against the investment account's, directly and case-insensitively.
// `linkableProfileId` is set for an existing (already-saved) profile so its positions link, and
// null for a new profile from the form, which has no positions page yet.
export const buildLocalComparisonRows = (
  profile: ProfileView,
  account: AccountView | null,
  layout: ComplianceObjectType,
  // The wizard/case already knows its own client, since a new profile has no client-owning
  // relationship of its own to read from.
  clientName?: string | null,
  // A not-yet-created profile has no positions or compliance programs of its own, so those rows
  // are omitted rather than showing as blank.
  includeActivityRows: boolean = true,
  linkableProfileId: number | null = null,
): ProfileMatchRow[] => {
  const accountCategoryLabel = account?.category ? ACCOUNT_TYPE_LABEL[account.category] : null;
  return [
    // Compared like any other local field: blank on either side is not comparable, and the two
    // sides can genuinely disagree (e.g. the wizard's known client vs. the account's own arena).
    localFieldRow("Client", clientName, account?.clientName),
    localFieldRow("Account type", ACCOUNT_TYPE_LABEL[profile.category], accountCategoryLabel),
    localFieldRow(
      ENTITY_TYPE_ROW_LABEL[layout],
      viewableAccountType(profile.accountType),
      account?.accountType ?? null,
    ),
    localFieldRow("Name", profile.name, account?.name),
    // Joint ownership has no tax id and only compares domicile at the country level.
    ...(layout === ComplianceObjectType.Joint
      ? [
          localFieldRow(
            "Domicile country",
            profile.domicileCountry,
            account?.domicileCountry ?? null,
          ),
        ]
      : [localTaxIdRow(profile, account), localAddressRow(profile, account)]),
    ...(includeActivityRows
      ? [
          positionsRow(profile.positionCount, account, linkableProfileId),
          complianceProgramsRow(profile.programs, account),
        ]
      : []),
  ];
};

const matchPercentFromRows = (rows: ProfileMatchRow[]): number | null => {
  const comparable = rows.filter((row) => row.status !== "na" && !row.excludeFromScore);
  if (comparable.length === 0) return null;
  const matched = comparable.filter((row) => row.status === "match").length;
  return (matched / comparable.length) * 100;
};

// Prefers the server's own match percentage when there is one; falls back to computing it from
// the rows for a new profile, which has no server-side match to read.
const resolveMatchPercent = (
  account: AccountView | null,
  rows: ProfileMatchRow[],
  serverMatchPercent?: number | null,
): number | null => {
  if (account === null) return null;
  return serverMatchPercent ?? matchPercentFromRows(rows);
};

export const buildNewProfileMatchData = (
  newProfile: WizardProfileData,
  investmentAccountNode: InvestmentAccountNode | null,
  resolveCountryName: CountryNameResolver,
  clientName?: string | null,
): ProfileMatchData => {
  const account = investmentAccountNode ? accountViewFromNode(investmentAccountNode) : null;
  const profile = profileViewFromForm(newProfile, resolveCountryName);
  const layout =
    categoryFromAccountType(
      accountTypeFromMainAccountType(investmentAccountNode?.accountType),
    ) ?? profile.category;
  // A not-yet-created profile has no positions or compliance programs of its own to show.
  const rows = buildLocalComparisonRows(profile, account, layout, clientName, false);
  return {
    matchPercent: resolveMatchPercent(account, rows),
    complianceProfileSubtitle: account !== null ? "Current selection" : null,
    investmentAccountSubtitle: "Current position",
    showInvestmentAccount: account !== null,
    rows,
  };
};

export const buildAssociatedProfileMatchData = (
  profileNode: ProfileNode,
  investmentAccountNode: InvestmentAccountNode | null,
  linkableProfileId: number | null,
): ProfileMatchData => {
  const account = investmentAccountNode ? accountViewFromNode(investmentAccountNode) : null;
  const layout =
    categoryFromAccountType(
      accountTypeFromMainAccountType(investmentAccountNode?.accountType),
    ) ?? profileCategoryFromNode(profileNode);
  const rows = buildServerComparisonRows(profileNode, account, layout, linkableProfileId);
  return {
    matchPercent: resolveMatchPercent(account, rows, profileNode.matchPercent),
    complianceProfileSubtitle: account !== null ? "Current selection" : null,
    investmentAccountSubtitle: "Current position",
    showInvestmentAccount: account !== null,
    rows,
  };
};

// Read by both the snapshot tab's warning icon and the in-view banner, so the two can't disagree.
export const hasProfileDiscrepancy = (matchData: ProfileMatchData | null): boolean =>
  matchData?.showInvestmentAccount === true &&
  matchData.matchPercent !== null &&
  matchData.matchPercent < 100;

// A profile not fetched against this investment account has no server-computed match to trust,
// so it shows only its own data, with nothing on the investment account side to compare against.
export const buildUnassociatedProfileMatchData = (
  profileNode: ProfileNode,
  clientName?: string | null,
): ProfileMatchData => {
  const profile = profileViewFromNode(profileNode);
  // An existing profile has an id, so its positions link even without an investment account.
  const rows = buildLocalComparisonRows(
    profile,
    null,
    profile.category,
    clientName,
    true,
    profileNode.id,
  );
  return {
    matchPercent: null,
    complianceProfileSubtitle: null,
    investmentAccountSubtitle: "Current position",
    showInvestmentAccount: false,
    rows,
  };
};
